import { PublicApiError } from "./http";
import {
  isParticipationApiId,
  type ParticipationApiId,
} from "./participation";

export const MAX_SERVER_REFRESH_WINDOW_MS = 5 * 24 * 60 * 60 * 1_000;
export const MAX_PROJECTION_REFRESH_DURATION_MS = 12 * 60 * 60 * 1_000;
export const MAX_PROJECTION_REFRESH_REQUEST_BYTES = 1_024;
export const PROJECTION_REFRESH_HEADER = "X-Zenyasai-Projection";
export const REFRESH_CONTROLLER_TOKEN_HEADER = "X-Zenyasai-Controller-Token";

export interface ParticipationRefreshCommand {
  readonly apiId: ParticipationApiId;
  readonly targetEpochMs: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function validateProjectionRefreshRequest(request: Request): void {
  const url = new URL(request.url);
  const origin = request.headers.get("Origin");
  const fetchSite = request.headers.get("Sec-Fetch-Site");

  if (url.search) {
    throw new PublicApiError(400, "INVALID_QUERY", "更新リクエストに検索条件は指定できません。");
  }
  if (
    origin !== url.origin
    || fetchSite !== "same-origin"
    || request.headers.get(PROJECTION_REFRESH_HEADER) !== "1"
  ) {
    throw new PublicApiError(403, "REFRESH_REQUEST_REJECTED", "更新リクエストを確認できませんでした。");
  }

  const contentType = request.headers.get("Content-Type")?.toLowerCase() ?? "";
  if (!contentType.startsWith("application/json")) {
    throw new PublicApiError(415, "UNSUPPORTED_MEDIA_TYPE", "JSON形式で送信してください。");
  }
}

export function validateProjectionRefreshPayload(
  value: unknown,
  nowEpochMs: number,
): ParticipationRefreshCommand {
  if (!isRecord(value) || Object.keys(value).length !== 2) {
    throw new PublicApiError(400, "INVALID_REFRESH_REQUEST", "更新条件が正しくありません。");
  }

  const apiId = value.apiId;
  const targetEpochMs = value.targetEpochMs;
  if (
    typeof apiId !== "string"
    || !isParticipationApiId(apiId)
    || typeof targetEpochMs !== "number"
    || !Number.isSafeInteger(targetEpochMs)
    || targetEpochMs <= nowEpochMs
    || targetEpochMs > nowEpochMs + MAX_PROJECTION_REFRESH_DURATION_MS
  ) {
    throw new PublicApiError(400, "INVALID_REFRESH_REQUEST", "更新条件が正しくありません。");
  }

  return { apiId, targetEpochMs };
}

export async function readProjectionRefreshBody(request: Request): Promise<unknown> {
  const contentLengthHeader = request.headers.get("Content-Length");
  if (contentLengthHeader !== null) {
    const contentLength = Number(contentLengthHeader);
    if (
      !Number.isSafeInteger(contentLength)
      || contentLength < 0
      || contentLength > MAX_PROJECTION_REFRESH_REQUEST_BYTES
    ) {
      await request.body?.cancel();
      throw new PublicApiError(413, "REQUEST_TOO_LARGE", "更新リクエストが大きすぎます。");
    }
  }

  if (!request.body) {
    throw new PublicApiError(400, "INVALID_REFRESH_REQUEST", "更新条件が正しくありません。");
  }

  const reader = request.body.getReader();
  const decoder = new TextDecoder();
  let receivedBytes = 0;
  let text = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      receivedBytes += value.byteLength;
      if (receivedBytes > MAX_PROJECTION_REFRESH_REQUEST_BYTES) {
        await reader.cancel();
        throw new PublicApiError(413, "REQUEST_TOO_LARGE", "更新リクエストが大きすぎます。");
      }
      text += decoder.decode(value, { stream: true });
    }
    text += decoder.decode();
  } finally {
    reader.releaseLock();
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new PublicApiError(400, "INVALID_JSON", "JSON形式が正しくありません。");
  }
}
